import { ApiError, errorMessage as apiErrorMessage } from '@nexo/api';
import { useState } from 'react';
import type { z } from 'zod';
import { t } from './i18n';

export type FieldErrors = Record<string, string | undefined>;

/**
 * Translates a message that may come in Spanish from shared code: the zod schemas in @nexo/types
 * and the fallbacks of @nexo/api. Their Spanish text is the key (see locales/en/app.json); anything
 * else (server messages, already in the interface language) is returned unchanged.
 */
export const translateMessage = (message: string): string => t(message);

/** `errorMessage` from @nexo/api, translated. */
export const errorMessage = (error: unknown): string => translateMessage(apiErrorMessage(error));

/** Validates with a shared zod schema and returns the first error per field (translated). */
export function validate<T extends z.ZodType>(
  schema: T,
  values: unknown,
): { data: z.output<T>; errors: null } | { data: null; errors: FieldErrors } {
  const result = schema.safeParse(values);
  if (result.success) return { data: result.data, errors: null };
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || '_';
    errors[key] ??= translateMessage(issue.message);
  }
  return { data: null, errors };
}

/** Maps server-side validation issues (400) onto form fields. */
export function errorsFromApi(error: unknown): FieldErrors | null {
  if (!(error instanceof ApiError) || !error.issues) return null;
  return Object.fromEntries(Object.entries(error.issues).map(([key, list]) => [key, list[0]]));
}

/** Tiny form-state helper: values, per-field errors and a setter. */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const set = <K extends keyof T>(key: K, value: T[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };
  return { values, set, setValues, errors, setErrors };
}
